import React, { useCallback, useEffect, useState } from 'react';
import {
  Container,
  Title,
  Text,
  Button,
  Group,
  Stack,
  Card,
  Badge,
  Alert,
  Loader,
  Progress,
  Code,
  Divider,
  Table,
  ActionIcon,
  Tooltip
} from '@mantine/core';
import {
  IconArrowLeft,
  IconAlertCircle,
  IconRefresh,
  IconExternalLink,
  IconCircleCheckFilled,
  IconCircleX,
  IconClock,
  IconPlayerStopFilled,
  IconDownload
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useNavigate, useParams } from 'react-router-dom';
import JSZip from 'jszip';
import { cancelBatch, getBatch, updateBatchProgress } from '../api_scripts/batchesClient';
import { getRun } from '../api_scripts/runsClient';
import { getReport, getReportPdf, getReportMarkdown } from '../api_scripts/reportsClient';

const POLL_INTERVAL_MS = 5000;
const TERMINAL_BATCH_STATUSES = new Set(['COMPLETED', 'FAILED', 'CANCELLED']);
// Cap parallelism on the report bundle so we don't burn through API Gateway's
// per-connection concurrency for a 30-run batch. 5 concurrent fetches is
// friendly and still fast — a 30-run batch downloads in ~6 waves.
const REPORT_BUNDLE_CONCURRENCY = 5;

function statusBadge(status) {
  switch (status) {
    case 'COMPLETED':
      return <Badge color="green" variant="light">COMPLETED</Badge>;
    case 'RUNNING':
      return <Badge color="blue" variant="light">RUNNING</Badge>;
    case 'QUEUED':
      return <Badge color="gray" variant="light">QUEUED</Badge>;
    case 'CANCELLED':
      return <Badge color="orange" variant="light">CANCELLED</Badge>;
    case 'FAILED':
    case 'ERROR':
      return <Badge color="red" variant="light">{status}</Badge>;
    case 'CALL_ENDED_PENDING_EVAL':
      // In-browser batch dispatcher moved on after the call hung up; reporter
      // (recording wait + Transcribe + evaluator) is still running. Will
      // reconcile to COMPLETED/FAILED once the TestRun row updates — the
      // per-run reconciler below refetches each testRunId while the batch
      // still has rows in this intermediate state.
      return <Badge color="violet" variant="light">EVALUATING…</Badge>;
    default:
      return <Badge variant="light">{status || '—'}</Badge>;
  }
}

function statusIcon(status) {
  if (status === 'COMPLETED') return <IconCircleCheckFilled size={16} style={{ color: 'var(--mantine-color-green-6)' }} />;
  if (status === 'FAILED' || status === 'ERROR') return <IconCircleX size={16} style={{ color: 'var(--mantine-color-red-6)' }} />;
  if (status === 'RUNNING' || status === 'PENDING' || status === 'CALL_ENDED_PENDING_EVAL') return <Loader size="xs" />;
  return <IconClock size={16} style={{ color: 'var(--mantine-color-gray-5)' }} />;
}

function BatchDetail({ onBack }) {
  const { batchId } = useParams();
  const navigate = useNavigate();
  const [batch, setBatch] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [bundling, setBundling] = useState(false);
  const [bundleProgress, setBundleProgress] = useState({ done: 0, total: 0 });

  const refresh = useCallback(async () => {
    if (!batchId) return;
    setLoading(true);
    setError(null);
    try {
      const row = await getBatch(batchId);
      setBatch(row);
    } catch (e) {
      setError(e?.response?.data?.message || e.message);
    } finally {
      setLoading(false);
    }
  }, [batchId]);

  useEffect(() => { refresh(); }, [refresh]);

  // Reconcile CALL_ENDED_PENDING_EVAL rows → real COMPLETED/FAILED verdict.
  // The in-browser batch dispatcher moves on when a call hangs up (see
  // batchRunner.js → waitForTerminal({ waitUntilCallEnded: true })), posting
  // CALL_ENDED_PENDING_EVAL as a placeholder finalStatus. The reporter Lambda
  // then runs for another 5-10 min doing Transcribe + evaluator + render,
  // eventually updating the TestRun row's status. This effect polls each
  // pending row's testRunId every 15s and POSTs the real finalStatus back
  // onto the batch when it lands. Self-stops when no rows are pending.
  useEffect(() => {
    if (!batchId || !batch) return undefined;
    const pending = (batch.results || []).filter(
      (r) => r?.testRunId && r?.finalStatus === 'CALL_ENDED_PENDING_EVAL'
    );
    if (pending.length === 0) return undefined;

    let cancelled = false;
    const TERMINAL = new Set(['COMPLETED', 'FAILED', 'ERROR', 'CANCELLED']);
    const RECONCILE_INTERVAL_MS = 15000;

    const tick = async () => {
      let anyUpdated = false;
      await Promise.all(
        pending.map(async (p) => {
          try {
            const row = await getRun(p.testRunId);
            if (!row?.status || !TERMINAL.has(row.status)) return;
            await updateBatchProgress(batchId, {
              planIndex: p.planIndex,
              name: p.name,
              testRunId: p.testRunId,
              finalStatus: row.status,
              startedAt: p.startedAt,
              finishedAt: new Date().toISOString()
            });
            anyUpdated = true;
          } catch {
            // transient — try again next tick
          }
        })
      );
      if (anyUpdated && !cancelled) refresh();
    };

    tick();
    const handle = setInterval(() => {
      if (!cancelled) tick();
    }, RECONCILE_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(handle);
    };
  }, [batch, batchId, refresh]);

  const handleCancel = useCallback(async () => {
    if (!batchId) return;
    // Guardrail — user picked cancel deliberately, so a single confirm is
    // enough. In-flight PSTN calls will continue to completion (Chime doesn't
    // support mid-call abort), but no new plans will dial.
    if (!window.confirm(
      'Cancel this batch?\n\n' +
      '• Queued plans will not dial.\n' +
      '• The currently in-flight call (if any) will finish.\n' +
      '• Already-completed reports are preserved.'
    )) return;
    setCancelling(true);
    try {
      const updated = await cancelBatch(batchId);
      setBatch(updated);
      notifications.show({
        title: 'Batch cancelled',
        message: `Any queued plans are skipped. ${updated.doneCount || 0} of ${updated.totalCount || 0} completed.`,
        color: 'orange'
      });
    } catch (e) {
      notifications.show({
        title: 'Cancel failed',
        message: e?.response?.data?.message ?? e.message,
        color: 'red'
      });
    } finally {
      setCancelling(false);
    }
  }, [batchId]);

  // Bulk-download every report belonging to this batch, bundle into a zip
  // organized by plan slug. Skips plans without a testRunId (never dialed) and
  // silently tolerates per-plan fetch failures — bad plans get a small
  // "_missing.txt" note in the zip so the engineer knows which slot skipped.
  const handleDownloadAllReports = useCallback(async () => {
    if (!batch) return;
    const runs = (batch.results || []).filter((r) => r?.testRunId);
    if (runs.length === 0) {
      notifications.show({
        title: 'Nothing to download',
        message: 'No plans in this batch have finished dialing yet.',
        color: 'yellow'
      });
      return;
    }
    setBundling(true);
    setBundleProgress({ done: 0, total: runs.length });
    const zip = new JSZip();
    const missing = [];

    const fetchOne = async (r) => {
      const plan = (batch.plans || []).find((p) => p.index === r.planIndex);
      const slug = (plan?.planId || plan?.name || `plan-${r.planIndex + 1}`)
        .replace(/[^A-Za-z0-9_-]+/g, '-')
        .slice(0, 60);
      const folder = zip.folder(`${String(r.planIndex + 1).padStart(2, '0')}_${slug}`);
      // Fetch all three formats in parallel; if any format fails we still
      // include whatever succeeded. PDF is the primary deliverable — plain
      // JSON and Markdown are secondary but useful for automation.
      const [pdfRes, mdRes, jsonRes] = await Promise.allSettled([
        getReportPdf(r.testRunId),
        getReportMarkdown(r.testRunId),
        getReport(r.testRunId)
      ]);
      if (pdfRes.status === 'fulfilled') folder.file(`report.pdf`, pdfRes.value);
      if (mdRes.status === 'fulfilled') folder.file(`report.md`, mdRes.value);
      if (jsonRes.status === 'fulfilled') {
        folder.file(`report.json`, JSON.stringify(jsonRes.value, null, 2));
      }
      if (
        pdfRes.status === 'rejected' &&
        mdRes.status === 'rejected' &&
        jsonRes.status === 'rejected'
      ) {
        missing.push(`${slug} (${r.testRunId}) — all formats failed`);
      }
      setBundleProgress((p) => ({ done: p.done + 1, total: p.total }));
    };

    // Simple concurrency limiter — split runs into waves of N.
    try {
      for (let i = 0; i < runs.length; i += REPORT_BUNDLE_CONCURRENCY) {
        const wave = runs.slice(i, i + REPORT_BUNDLE_CONCURRENCY);
        await Promise.all(wave.map(fetchOne));
      }
      if (missing.length > 0) {
        zip.file('_missing.txt', missing.join('\n'));
      }
      const blob = await zip.generateAsync({ type: 'blob', compression: 'DEFLATE' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `sit-batch-${batchId}-reports.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      notifications.show({
        title: 'Bundle ready',
        message: `Downloaded ${runs.length - missing.length} report(s)${
          missing.length > 0 ? ` · ${missing.length} skipped` : ''
        }`,
        color: missing.length > 0 ? 'yellow' : 'green'
      });
    } catch (e) {
      notifications.show({
        title: 'Bundle failed',
        message: e?.message ?? String(e),
        color: 'red'
      });
    } finally {
      setBundling(false);
      setBundleProgress({ done: 0, total: 0 });
    }
  }, [batch, batchId]);

  // Auto-poll while the batch is still in flight. Stops when COMPLETED /
  // FAILED so we're not hammering DDB for done batches.
  useEffect(() => {
    if (!batch || TERMINAL_BATCH_STATUSES.has(batch.status)) return;
    const t = setInterval(refresh, POLL_INTERVAL_MS);
    return () => clearInterval(t);
  }, [batch, refresh]);

  if (!batch && error) {
    return (
      <Container size="lg" py="lg">
        <Group mb="md">
          <Button variant="subtle" leftSection={<IconArrowLeft size={16} />} onClick={onBack}>
            Back
          </Button>
        </Group>
        <Alert color="red" icon={<IconAlertCircle size={16} />}>{error}</Alert>
      </Container>
    );
  }
  if (!batch) {
    return (
      <Container size="lg" py="lg">
        <Group gap="sm">
          <Loader size="sm" />
          <Text size="sm" c="dimmed">Loading batch…</Text>
        </Group>
      </Container>
    );
  }

  const pct = batch.totalCount > 0 ? (batch.doneCount / batch.totalCount) * 100 : 0;
  const results = batch.results || [];
  const plansByIndex = new Map((batch.plans || []).map((p) => [p.index, p]));
  const completedRuns = results.filter((r) => r.finalStatus === 'COMPLETED').length;
  const failedRuns = results.filter((r) => r.finalStatus === 'FAILED' || r.finalStatus === 'ERROR').length;

  return (
    <Container size="lg" py="lg">
      <Group justify="space-between" mb="md">
        <Group>
          <Button variant="subtle" leftSection={<IconArrowLeft size={16} />} onClick={onBack}>
            Back to History
          </Button>
        </Group>
        <Group gap="xs">
          {!TERMINAL_BATCH_STATUSES.has(batch.status) && (
            <Button
              variant="light"
              color="orange"
              leftSection={<IconPlayerStopFilled size={14} />}
              onClick={handleCancel}
              loading={cancelling}
              disabled={cancelling}
            >
              Cancel batch
            </Button>
          )}
          <Button
            variant="light"
            color="violet"
            leftSection={<IconDownload size={14} />}
            onClick={handleDownloadAllReports}
            loading={bundling}
            disabled={bundling || (batch.results || []).filter((r) => r?.testRunId).length === 0}
          >
            {bundling
              ? `Bundling ${bundleProgress.done}/${bundleProgress.total}…`
              : 'Download all reports'}
          </Button>
          <Tooltip label="Refresh now">
            <ActionIcon variant="light" onClick={refresh} loading={loading}>
              <IconRefresh size={16} />
            </ActionIcon>
          </Tooltip>
        </Group>
      </Group>

      <Stack gap="md">
        <Group gap="sm">
          <Title order={2}>Batch run</Title>
          {statusBadge(batch.status)}
          {!TERMINAL_BATCH_STATUSES.has(batch.status) && (
            <Text size="xs" c="dimmed">auto-refreshing every 5s…</Text>
          )}
        </Group>

        <Card withBorder padding="md" radius="md">
          <Group justify="space-between" mb="xs">
            <Group gap="xs">
              {batch.label && <Text fw={600}>{batch.label}</Text>}
              <Text size="sm" c="dimmed">
                {batch.doneCount} / {batch.totalCount} done · {completedRuns} passed · {failedRuns} failed
              </Text>
            </Group>
            <Code>{batch.batchId}</Code>
          </Group>
          <Progress
            value={pct}
            color={batch.status === 'COMPLETED' ? 'green' : 'violet'}
            animated={!TERMINAL_BATCH_STATUSES.has(batch.status)}
          />
          <Group gap="xs" mt="xs">
            <Text size="xs" c="dimmed">created {new Date(batch.createdAt).toLocaleString()}</Text>
            <Text size="xs" c="dimmed">by {batch.createdBy || 'unknown'}</Text>
            {batch.overrides?.destination && (
              <Badge size="xs" variant="light" color="gray">dest: {batch.overrides.destination}</Badge>
            )}
          </Group>
        </Card>

        <Divider label="Plans" labelPosition="left" />

        <Card withBorder padding={0} radius="md" style={{ overflow: 'hidden' }}>
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th style={{ width: 32 }}></Table.Th>
                <Table.Th style={{ width: 50 }}>#</Table.Th>
                <Table.Th>Plan</Table.Th>
                <Table.Th style={{ width: 110 }}>Status</Table.Th>
                <Table.Th style={{ width: 140 }}>Finished</Table.Th>
                <Table.Th style={{ width: 80 }}></Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {(batch.plans || []).map((p) => {
                const r = results.find((x) => x.planIndex === p.index);
                return (
                  <Table.Tr key={p.index}>
                    <Table.Td>{statusIcon(r?.finalStatus || (r ? 'RUNNING' : 'QUEUED'))}</Table.Td>
                    <Table.Td><Text size="xs" c="dimmed">{p.index + 1}</Text></Table.Td>
                    <Table.Td>
                      <Stack gap={0}>
                        <Text size="sm" fw={500} lineClamp={1}>{p.name || plansByIndex.get(p.index)?.name}</Text>
                        {r?.testRunId && <Code style={{ fontSize: 10 }}>{r.testRunId}</Code>}
                      </Stack>
                    </Table.Td>
                    <Table.Td>{statusBadge(r?.finalStatus || (r ? 'RUNNING' : 'QUEUED'))}</Table.Td>
                    <Table.Td>
                      <Text size="xs" c="dimmed">
                        {r?.finishedAt ? new Date(r.finishedAt).toLocaleTimeString() : '—'}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      {r?.testRunId && (
                        <Tooltip label="Open report">
                          <ActionIcon
                            variant="subtle"
                            onClick={() => navigate(`/history/${r.testRunId}`)}
                          >
                            <IconExternalLink size={14} />
                          </ActionIcon>
                        </Tooltip>
                      )}
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        </Card>
      </Stack>
    </Container>
  );
}

export default BatchDetail;
